import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import {
    Box, DialogContent, DialogTitle,
    FormControlLabel, Switch, Typography,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { DisabledFormWrapper } from 'app/main/util/clusterUtils';

import * as formHelpers from './formHelpers';
import { isN1Instance } from './formHelpers';
import GcpAcceleratorFields from './GcpAcceleratorFields';
import useClusterForm from '../hooks/useClusterForm';
import useGcpAccelerators from '../hooks/useGcpAccelerators';
import ClusterFormHeader from '../components/ClusterFormHeader';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import BasicInformationSection from '../components/BasicInformationSection';
import ResourceManagerConfiguration from '../components/ResourceManagerConfiguration';
import StorageSettingsSection from '../components/StorageSettingsSection';
import CredentialSelector from '../components/CredentialSelector';
import RegionSelector from '../components/RegionSelector';
import ZoneSelector from '../components/ZoneSelector';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import SectionCard from '../components/SectionCard';
import CapacityReservationsSection from '../components/CapacityReservationsSection';
import DescriptionSection from '../components/DescriptionSection';
import ComputeNodesSection from '../components/ComputeNodesSection';
import ComputeNodeGroupFields from '../components/ComputeNodeGroupFields';
import { ZoneAvailabilityIndicator } from '../components/ZoneAvailabilityComponents';
import { inputGroupStyle, columnContainerStyle, columnStyle } from '../components/formLayoutStyles';
import TpuSection from './TpuSection';

const GcpCluster = (props) => {
    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace,
        disableInputs, isFormValid, formInvalidReason, volSizeLimits,
        availability, quotas, handleClose, handleSubmit, handleEdit,
    } = useClusterForm({
        provider: 'gcp', props, formHelpers,
        extraValidation: (f) => Boolean(f.zoneId),
    });

    const {
        isLoading: isAvailabilityLoading, error: availabilityError,
        unableToVerify, selectedZoneAvailable, selectedZoneMissing,
    } = availability;

    // GCP cost estimate
    const providers = useSelector(({ providers }) => providers.providers.list);
    const costEstimate = useMemo(() => {
        if (!form?.regionId || !providers?.length) return null;
        const gcp = providers.find(p => p.id === 'gcp');
        if (!gcp?.instanceSizes) return null;
        let total = 0;
        const getCost = (id) => gcp.instanceSizes.find(s => s.instance_id === id)?.cost?.cost_region?.[form.regionId] || 0;
        total += getCost(form.serverInstanceId);
        (form.nodeGroups || []).forEach(g => { total += getCost(g.instance_id) * parseInt(g.count || 0, 10); });
        return total > 0 ? total.toFixed(2) : null;
    }, [form?.regionId, form?.serverInstanceId, form?.nodeGroups, providers]);

    // Live GPU accelerators attachable in the selected zone (N1 shapes). Fetched
    // once and shared by the head node + every compute group.
    const accel = useGcpAccelerators({
        credentialId: form?.credentialId,
        region: form?.regionId,
        zone: form?.zoneId,
        enabled: Boolean(form?.credentialId && form?.zoneId),
    });

    // GCP catalog entries — used to show the fixed GPU baked into a2/g2/a3 shapes.
    const gcpInstances = useMemo(() => {
        const gcp = providers?.find(p => p.id === 'gcp');
        return (gcp && gcp.instanceSizes) || [];
    }, [providers]);
    const findInstance = useCallback((id) => gcpInstances.find(s => s.instance_id === id) || null, [gcpInstances]);

    // GCP: clear accelerators for non-n1 instances
    const handleServerInstanceChange = useCallback((instanceId) => {
        const changes = { serverInstanceId: instanceId };
        if (!isN1Instance(instanceId)) { changes.serverAcceleratorType = null; changes.serverAcceleratorCount = 0; }
        setField(changes);
    }, [setField]);

    const handleComputeInstanceChange = useCallback((index, instanceId) => {
        const updated = [...(form?.nodeGroups || [])];
        const g = { ...updated[index], instance_id: instanceId };
        if (!isN1Instance(instanceId)) { g.accelerator_type = null; g.accelerator_count = 0; }
        updated[index] = g;
        setField('nodeGroups', updated);
    }, [form?.nodeGroups, setField]);

    const renderGcpGroupFields = useCallback((group, index, { updateGroup }) => (
        <ComputeNodeGroupFields
            group={group} index={index} updateGroup={updateGroup}
            provider="gcp" imageMode={form?.imageMode} maxCount={250}
            onInstanceChange={handleComputeInstanceChange}
            allowedInstanceIds={marketplace.allowedComputeIds}
            disableStackFields={marketplace.disableStackFields}
            disabled={disableInputs}
        >
            <GcpAcceleratorFields
                instanceId={group.instance_id}
                instance={findInstance(group.instance_id)}
                acceleratorType={group.accelerator_type}
                acceleratorCount={group.accelerator_count}
                onTypeChange={(type) => {
                    updateGroup(index, 'accelerator_type', type);
                    if (!type) updateGroup(index, 'accelerator_count', 0);
                    else if (!group.accelerator_count) updateGroup(index, 'accelerator_count', 1);
                }}
                onCountChange={(n) => updateGroup(index, 'accelerator_count', n)}
                options={accel.acceleratorTypes}
                loading={accel.loading}
                zoneSelected={Boolean(form?.zoneId)}
                unableToVerify={accel.unableToVerify}
                disabled={disableInputs}
                classes={classes}
            />
        </ComputeNodeGroupFields>
    ), [form?.imageMode, form?.zoneId, marketplace, handleComputeInstanceChange, disableInputs, classes, accel, findInstance]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="gcp" providerLabel="Google Cloud" marketplace={marketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isFormValid} formInvalidReason={formInvalidReason} loading={loading} disableInputs={disableInputs}
                            formType={props.type} onSave={handleSubmit} onEdit={handleEdit} onClose={handleClose}
                            subtitle={costEstimate ? `— Est. $${costEstimate}/hr` : undefined}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <div className="!w-full">
                                <BasicInformationSection
                                    form={form} setField={setField} provider="gcp"
                                    allowedOsTypes={marketplace.allowedOsTypes || undefined}
                                    disableStackFields={marketplace.disableStackFields}
                                />

                                <div style={columnContainerStyle}>
                                    <div style={columnStyle}>
                                        <SectionCard icon="cloud" title="Infrastructure" fillHeight
                                            action={form.zoneId ? (
                                                <ZoneAvailabilityIndicator
                                                    isLoading={isAvailabilityLoading} isAvailable={selectedZoneAvailable}
                                                    missingInstances={selectedZoneMissing} error={availabilityError}
                                                    unableToVerify={unableToVerify}
                                                />
                                            ) : null}
                                        >
                                            <div style={inputGroupStyle}>
                                                <CredentialSelector provider="gcp" value={form.credentialId}
                                                    onChange={(id) => setField('credentialId', id)} />
                                                <InstanceSizeSelector provider="gcp" value={form.serverInstanceId || ''}
                                                    onChange={handleServerInstanceChange}
                                                    allowedInstanceIds={marketplace.config?.instance_type} label="Head Node Size" />
                                            </div>
                                            <div style={inputGroupStyle}>
                                                <RegionSelector provider="gcp" value={form.regionId || ''} onChange={(id) => setField('regionId', id)} />
                                                <ZoneSelector provider="gcp" regionId={form.regionId || ''} value={form.zoneId || ''} onChange={(id) => setField('zoneId', id)} />
                                            </div>
                                            {/* Accelerator sits below region/zone so a zone is picked first and
                                                the GPU list reflects live availability in that zone. */}
                                            <GcpAcceleratorFields
                                                instanceId={form.serverInstanceId}
                                                instance={findInstance(form.serverInstanceId)}
                                                acceleratorType={form.serverAcceleratorType}
                                                acceleratorCount={form.serverAcceleratorCount}
                                                onTypeChange={(type) => setField({ serverAcceleratorType: type, serverAcceleratorCount: type ? (form.serverAcceleratorCount || 1) : 0 })}
                                                onCountChange={(n) => setField('serverAcceleratorCount', n)}
                                                options={accel.acceleratorTypes}
                                                loading={accel.loading}
                                                zoneSelected={Boolean(form.zoneId)}
                                                unableToVerify={accel.unableToVerify}
                                                disabled={disableInputs}
                                                classes={classes}
                                            />
                                            {advanced && (
                                                <Box mt={2} display="flex" alignItems="center">
                                                    <FormControlLabel
                                                        control={<Switch checked={form.spotVm || false} onChange={(e) => setField('spotVm', e.target.checked)} color="primary" />}
                                                        label={<Typography variant="body2">Enable Spot/Preemptible Instances</Typography>}
                                                    />
                                                </Box>
                                            )}
                                        </SectionCard>
                                    </div>
                                    <div style={columnStyle}>
                                        <ResourceManagerConfiguration form={form} setField={setField} hideBursting={marketplace.isMarketStack} lockManager={Boolean(marketplace.config?.resource_manager)} />
                                    </div>
                                </div>

                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="gcp" advanced={advanced} marketplace={marketplace} disableInputs={disableInputs}
                                />
                                {advanced && <CapacityReservationsSection form={form} setField={setField} fillHeight showZoneFallback />}
                                {advanced && <StorageSettingsSection form={form} setField={setField} provider="gcp" volSizeLimits={volSizeLimits} />}

                                <ComputeNodesSection form={form} setField={setField} disabled={disableInputs} maxGroups={250} renderGroupFields={renderGcpGroupFields} />
                                {advanced && <TpuSection form={form} setField={setField} />}
                                <DescriptionSection form={form} setField={setField} />
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default GcpCluster;
